import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {User} from '../../user/shared/user.model';
import {createStore, select, setProps, withProps} from '@ngneat/elf';
import {AuthProps} from './interfaces/auth-props.interface';
import {persistState, sessionStorageStrategy} from '@ngneat/elf-persist-state';
import {AuthService} from '../shared/services/auth.service';
import {ResponseToken} from '../shared/interfaces/auth-user-data.interface';
import {
  EMPTY_PERSONAL_DATA,
  EMPTY_POST,
  EMPTY_RESPONSE_TOKEN,
  LENGTH_3,
} from 'src/environments/constants';
import {
  Family,
  Institution,
  PersonalData,
  Residence,
} from '../../user/shared/options.model';
import {Comentario, Post} from '../../user/shared/post.model';

@Injectable({providedIn: 'root'})
export class AuthRepository {
  $user: Observable<User | null>;
  private readonly authStore;
  private id!: string;
  private newCode!: string;
  private currentCode!: string;
  private recaptcha!: string;
  private publicIp!: string;
  private transactionType!: string;
  private readonly personalData = new BehaviorSubject<PersonalData>(
    EMPTY_PERSONAL_DATA
  );
  public readonly personalData$: Observable<PersonalData> =
    this.personalData.asObservable();

  constructor(@Inject(LOCALE_ID) public locale: string) {
    this.authStore = createStore(
      {name: 'auth'},
      withProps<AuthProps>({
        user: null,
      })
    );

    persistState(this.authStore, {
      key: 'auth',
      storage: sessionStorageStrategy,
    });

    this.$user = this.authStore.pipe(select(state => state.user));
  }

  getAccessTokenValue() {
    return this.authStore.getValue().user?.token;
  }

  getMenuOptions() {
    return this.authStore.getValue().user?.menuOptions;
  }

  setUser(user: User) {
    this.authStore.update(state => ({
      ...state,
      user,
    }));
  }

  isLoggedIn() {
    return this.authStore.pipe(select(state => !!state.user?.token));
  }

  isLoggedInValue(): boolean {
    try {
      const token = this.getAccessTokenValue();
      if (token) {
        return !!AuthService.decodeToken(token);
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  clear() {
    this.authStore.update(setProps({user: null}));
  }

  public setId(id: string): void {
    this.id = id;
    sessionStorage.setItem('id', id);
  }

  public getId(): string {
    if (!this.id) {
      this.id = sessionStorage.getItem('id') || '';
    }
    return this.id;
  }
  public setPost(post: Post): void {
    sessionStorage.setItem('post', JSON.stringify(post));
  }

  public getPost(): Post {
    const comments = sessionStorage.getItem('post');
    return comments ? JSON.parse(comments) : EMPTY_POST;
  }

  public setPersonalData(personalData: PersonalData): void {
    this.personalData.next(personalData);
  }

  public setPersonalDataInSessionStorage(personalData: PersonalData): void {
    sessionStorage.setItem('personalData', JSON.stringify(personalData));
  }

  public getPersonalDataFromSessionStorage(): PersonalData {
    const personalData = sessionStorage.getItem('personalData');
    return personalData ? JSON.parse(personalData) : EMPTY_PERSONAL_DATA;
  }
  public setResidences(family: Residence[]): void {
    sessionStorage.setItem('residence', JSON.stringify(family));
  }

  public getResidences(): Residence[] {
    const residence = sessionStorage.getItem('residence');
    return residence ? JSON.parse(residence) : [];
  }
  public setFamily(family: Family[]): void {
    sessionStorage.setItem('family', JSON.stringify(family));
  }

  public getFamily(): Family[] {
    const family = sessionStorage.getItem('family');
    return family ? JSON.parse(family) : [];
  }
  public setAcademicData(institutions: Institution[]): void {
    sessionStorage.setItem('institutions', JSON.stringify(institutions));
  }

  public getAcademicData(): Institution[] {
    const institutions = sessionStorage.getItem('institutions');
    return institutions ? JSON.parse(institutions) : [];
  }
  public setTokenRecaptcha(recaptcha: string): void {
    this.recaptcha = recaptcha;
  }
  public getTokenRecaptcha(): string {
    return this.recaptcha;
  }

  public setResponseToken(responseToken: ResponseToken): void {
    sessionStorage.setItem('responseToken', JSON.stringify(responseToken));
  }

  public getResponseToken(): ResponseToken {
    const tokenStr = sessionStorage.getItem('responseToken');
    return tokenStr ? JSON.parse(tokenStr) : EMPTY_RESPONSE_TOKEN;
  }

  public setPublicIp(publicIp: string): void {
    this.publicIp = publicIp;
  }
  public getPublicIp(): string {
    return this.publicIp;
  }

  public setPasswordTransactionType(transactionType: string): void {
    this.transactionType = transactionType;
  }
  public getPasswordTransactionType(): string {
    return this.transactionType;
  }
  public setNewCode(newCode: string): void {
    this.newCode = newCode;
  }
  public getNewCode(): string {
    return this.newCode;
  }
  public setCurrentCode(currentCode: string): void {
    this.currentCode = currentCode;
  }
  public getcurrentCode(): string {
    return this.currentCode;
  }

  public formatId(value: string): string {
    const trimmed = value.replace(/^0+/, '');
    if (trimmed === '') {
      return '';
    }

    const buf: string[] = [];
    let count = 0;
    for (let i = trimmed.length - 1; i >= 0; i--) {
      buf.push(trimmed[i]);
      count++;
      if (count === LENGTH_3 && i > 0) {
        buf.push('.');
        count = 0;
      }
    }

    // 3) Invertir manualmente sin usar `reverse()`
    const inverted: string[] = [];
    for (let i = buf.length - 1; i >= 0; i--) {
      inverted.push(buf[i]);
    }

    // 4) Unir en una cadena y devolver
    return inverted.join('');
  }
}
